import { INTERNAL_LINKS } from "@app/constants";
import { configSingleStory } from "@pub/config";
import placeholders from "@pub/placeholders";
import {
  ResponsiveImage,
  Typography,
  useCustomClassContext,
} from "@sphtech/dmg-design-system";
import { getGeniusLink } from "@src/app/utils/trackers/skimlinks";
import cx from "classnames";

import { ResolvedImageCUEElement, resolveImageCrop } from "../elements";
import styles from "../Storyline.module.css";
import { Annotation } from "./Annotation/Annotation";
import { getParsedUrl } from "./utils";

const Image = ({
  element,
  bodyMaxWidth,
}: {
  element: ResolvedImageCUEElement;
  bodyMaxWidth: number;
}) => {
  const customClass = useCustomClassContext();

  const crop = resolveImageCrop(
    element,
    configSingleStory.storylineImage?.fixedCropSelection,
  );
  if (!crop) return null;

  const caption = element.additionalFields.displayCaption?.value
    ? element.additionalFields.caption
    : undefined;

  // displayCredit is new field, if undefined in CaaS, then display credit
  const credit =
    typeof element.additionalFields.displayCredit === "undefined" ||
    element.additionalFields.displayCredit.value
      ? crop.credit
      : undefined;

  const description = element.additionalFields.description;
  const { imageLink } = element.additionalFields;

  const imageElement = (
    <ResponsiveImage
      src={crop.src}
      alt={crop.alt}
      srcWidth={crop.srcWidth}
      srcHeight={crop.srcHeight}
      displayWidth={bodyMaxWidth}
      loading="lazy"
      errorSrc={placeholders.landscape.src}
    />
  );

  const linkHostname = getParsedUrl(imageLink?.value)?.hostname;
  const isInternalLink =
    !!linkHostname && INTERNAL_LINKS.includes(linkHostname);
  const htmlAnchor = element.additionalFields.htmlAnchor?.value;

  return (
    <figure
      id={htmlAnchor}
      className={cx(
        styles.figure,
        // disabled, need to discuss proper solution
        // crop.srcHeight > crop.srcWidth && styles.figureNarrow,
      )}
    >
      {configSingleStory.storylineImage?.hyperlink !== false &&
      imageLink?.value ? (
        <a
          href={getGeniusLink(imageLink.value)}
          target="_blank"
          rel={
            isInternalLink
              ? "noreferrer noopener"
              : "noreferrer nofollow noopener"
          }
        >
          {imageElement}
        </a>
      ) : (
        imageElement
      )}
      {(caption?.value || description?.value || credit) && (
        <figcaption className={styles.figureCaptions}>
          {caption && (
            <p
              className={cx(
                styles.imageCaption,
                customClass?.figureCaptions?.imageCaption,
              )}
            >
              <Annotation element={caption} />
            </p>
          )}
          {/* Description is different from caption */}
          {description?.value && (
            <div className={styles.imageDescription}>
              <Typography.Body>
                <Annotation element={description} />
              </Typography.Body>
            </div>
          )}
          {credit && (
            <Typography.Disclaimer>
              {element.additionalFields.credit?.annotations?.length ? (
                <Annotation element={element.additionalFields.credit} />
              ) : (
                crop.credit
              )}
            </Typography.Disclaimer>
          )}
        </figcaption>
      )}
    </figure>
  );
};

export default Image;
